import { ReactSearchAutocomplete } from "react-search-autocomplete";
import styles from "../ourCars.module.css";
import {
  ACTIVE_PRICE_PATH,
  MPH_PATH,
  POWER_PATH,
  rangeFilterURLMap,
  RETAIL_PRICE_GBP_PATH,
  SORT_FILTER_OPTIONS,
  TOP_SPEED_PATH,
  urlRangeFilterMap,
} from "../ourCars-constants";
import SelectDropdown from "../../../../../../components/Dropdown/dropdown";
import { useEffect, useRef, useState } from "react";
import "rc-slider/assets/index.css";
import CarStyles from "./carStyles";
import CarMakes from "./carMakes";
import { access } from "../../../../../../common-utils";
import { Car, CarFiltersProps, RangeFilterURLMapProps, URLRangeFilterMapProps } from "../types";
import { useAppDispatch, useAppSelector } from "../../../../../../redux/hooks";
import {
  publishBHP,
  publishFilteredCars,
  publishFilterValues,
  publishRRP,
  publishTicketPrice,
  publishTopMPH,
  publishZeroTo60,
} from "../../../../../../redux/features/cars/carsSlice";
import FiltersForSmallerScreens from "./ourCarsFilters-sm";
import RangeFilter from "./rangeFilters";
import { ActionCreatorWithOptionalPayload } from "@reduxjs/toolkit";

const publishAction: {
  [key: string]: ActionCreatorWithOptionalPayload<
    {
      value: number[];
    },
    string
  >;
} = {
  [ACTIVE_PRICE_PATH]: publishTicketPrice,
  [RETAIL_PRICE_GBP_PATH]: publishRRP,
  [MPH_PATH]: publishTopMPH,
  [TOP_SPEED_PATH]: publishZeroTo60,
  [POWER_PATH]: publishBHP,
};

const OurCarsFilters = ({
  makeList,
  styleList,
  ticketPriceRange,
  rrpRange,
  topMPHRange,
  zeroTo60Range,
  bhpRange,
  carPrizes,
  ithInView,
}: CarFiltersProps) => {
  const urlFilterParam = useRef(location.hash);
  let selectedFilter = useAppSelector(state => state.cars.filter);
  const initialTicketPriceRange = [ticketPriceRange.min, ticketPriceRange.max];
  const initialRetailPriceGBPRange = [rrpRange.min, rrpRange.max];
  const initialTopMPHRange = [topMPHRange.min, topMPHRange.max];
  const initialZeroTo60Range = [zeroTo60Range.min, zeroTo60Range.max];
  const initialPowerRange = [bhpRange.min, bhpRange.max];
  const [sortByDropdownValue, setSortByDropdownValue] = useState("Sort By");
  const [moreFiltersVisible, setMoreFiltersVisible] = useState(false);
  const searchedCar = useRef("");
  useEffect(() => {
    getPreselectedMakeOrStyle();
    getPreselectedRangeValuesAndCar();
  }, []);
  const headerHeight = document.querySelector("header").clientHeight - 1;
  const initialRangeFilterParams = {
    ticketPriceValue: initialTicketPriceRange,
    rrpValue: [initialRetailPriceGBPRange[0] / 1000, initialRetailPriceGBPRange[1] / 1000],
    topMPHValue: initialTopMPHRange,
    zeroTo60Value: initialZeroTo60Range,
    bhpValue: initialPowerRange,
  };
  const rangeFilters = useRef(initialRangeFilterParams);
  const dispatch = useAppDispatch();
  const getFilteredCars = (
    selectedCar: string,
    selectedFilter: { category: keyof Car; value: string },
    {
      ticketPriceValue,
      rrpValue,
      topMPHValue,
      zeroTo60Value,
      bhpValue,
    }: {
      ticketPriceValue: number[];
      rrpValue: number[];
      topMPHValue: number[];
      zeroTo60Value: number[];
      bhpValue: number[];
    }
  ) =>
    carPrizes.filter(carPrize => {
      const matchesCars =
        selectedCar === "" ||
        `${carPrize.Make} ${carPrize.Model}` === selectedCar ||
        carPrize.Make === selectedCar ||
        carPrize.Styles.includes(selectedCar);
      const activePriceValue = access(ACTIVE_PRICE_PATH, carPrize);
      const retailPriceValue = access(RETAIL_PRICE_GBP_PATH, carPrize) / 1000;
      const topSpeedValue = access(TOP_SPEED_PATH, carPrize);
      const mphValue = access(MPH_PATH, carPrize);
      const powerValue = access(POWER_PATH, carPrize);
      const matchesRange =
        activePriceValue >= ticketPriceValue[0] &&
        activePriceValue <= ticketPriceValue[1] &&
        retailPriceValue >= rrpValue[0] &&
        retailPriceValue <= rrpValue[1] &&
        topSpeedValue >= topMPHValue[0] &&
        topSpeedValue <= topMPHValue[1] &&
        mphValue >= zeroTo60Value[0] &&
        mphValue <= zeroTo60Value[1] &&
        powerValue >= bhpValue[0] &&
        powerValue <= bhpValue[1];
      if (selectedFilter === null) {
        return matchesCars && matchesRange;
      }
      const { category, value } = selectedFilter;
      const matchesSelectedFilter = category === "Styles" ? carPrize.Styles.includes(value) : carPrize.Make === value;
      return matchesCars && matchesRange && matchesSelectedFilter;
    });

  const getDefaultRangeSliderValue = (path: string) => {
    switch (path) {
      case ACTIVE_PRICE_PATH:
        return initialTicketPriceRange;
      case RETAIL_PRICE_GBP_PATH:
        return [initialRetailPriceGBPRange[0] / 1000, initialRetailPriceGBPRange[1] / 1000];
      case MPH_PATH:
        return initialTopMPHRange;
      case TOP_SPEED_PATH:
        return initialZeroTo60Range;
      case POWER_PATH:
        return initialPowerRange;
    }
  };

  const getParams = (path: string, range: number[]) => {
    switch (path) {
      case ACTIVE_PRICE_PATH:
        rangeFilters.current = {
          ...rangeFilters.current,
          ticketPriceValue: range.length > 0 ? range : initialTicketPriceRange,
        };
        break;
      case RETAIL_PRICE_GBP_PATH:
        rangeFilters.current = {
          ...rangeFilters.current,
          rrpValue:
            range.length > 0 ? range : [initialRetailPriceGBPRange[0] / 1000, initialRetailPriceGBPRange[1] / 1000],
        };
        break;
      case MPH_PATH:
        rangeFilters.current = {
          ...rangeFilters.current,
          topMPHValue: range.length > 0 ? range : initialTopMPHRange,
        };
        break;
      case TOP_SPEED_PATH:
        rangeFilters.current = {
          ...rangeFilters.current,
          zeroTo60Value: range.length > 0 ? range : initialZeroTo60Range,
        };
        break;
      case POWER_PATH:
        rangeFilters.current = {
          ...rangeFilters.current,
          bhpValue: range.length > 0 ? range : initialPowerRange,
        };
        break;
    }
    return rangeFilters.current;
  };

  const onRangeChange = (path: string, range: number[]) => {
    const rangeParams = getParams(path, range);
    const filteredCarPrizes = getFilteredCars(searchedCar.current, selectedFilter, rangeParams);
    const defaultValue = getDefaultRangeSliderValue(path);
    let isDefaultValueSelected = true;
    for (let i = 0; i < range.length; i++) {
      if (range[i] !== defaultValue[i]) {
        isDefaultValueSelected = false;
        break;
      }
    }
    updateURLWithFilterParameters(
      rangeFilterURLMap[path as keyof RangeFilterURLMapProps],
      `${range[0]};${range[1]}`,
      isDefaultValueSelected
    );
    dispatch(publishAction[path]({ value: range }));
    dispatch(publishFilteredCars({ cars: filteredCarPrizes }));
  };

  const getPreselectedMakeOrStyle = () => {
    const dummyMakeSpan = document.querySelector(".pre-select-make") as HTMLSpanElement;
    if (dummyMakeSpan && dummyMakeSpan.textContent) {
      const selectedMake = dummyMakeSpan.textContent;
      onFilterChange("Make", selectedMake);
    }
    const dummyStyleSpan = document.querySelector(".pre-select-style") as HTMLSpanElement;
    if (dummyStyleSpan && dummyStyleSpan.textContent) {
      const selectedStyle = dummyStyleSpan.textContent;
      onFilterChange("Styles", selectedStyle);
    }
  };

  const getPreselectedRangeValuesAndCar = () => {
    if (location.hash) {
      const rangeFilterKeys = ["retailprice", "ticketprice", "topspeed", "zero60", "power"];
      const selectedFilters = location.hash.split("#!/")[1];
      const filterArray = selectedFilters.split("&");
      filterArray.forEach(filter => {
        const [key, value] = filter.split("=");
        if (rangeFilterKeys.includes(key)) {
          onRangeChange(
            urlRangeFilterMap[key as keyof URLRangeFilterMapProps],
            value.split(";").map(val => Number(val))
          );
        } else {
          if (key === "sort") {
            onSort(value);
          } else if (key === "search") {
            onCarSelect(value.replaceAll("%20", " "));
          }
        }
      });
    }
  };

  const updateURLWithFilterParameters = (key: string, value: string, defaultFilterSelected = false) => {
    if (defaultFilterSelected) {
      if (urlFilterParam.current.indexOf("#!/&") >= 0) {
        const regex = new RegExp(`(&${key}=)[^&]+`);
        urlFilterParam.current = urlFilterParam.current.replace(regex, "");
      } else {
        const regex = new RegExp(`(${key}=)[^&]+`);
        urlFilterParam.current = urlFilterParam.current.replace(regex, "");
      }
    } else {
      if (urlFilterParam.current === "") {
        urlFilterParam.current += "#!/";
      } else {
        urlFilterParam.current += "&";
      }
      const index = urlFilterParam.current.indexOf(`${key}=`);
      if (index >= 0) {
        updateFilterValueInURL(key, value);
      } else {
        urlFilterParam.current += `${key}=${value}`;
      }
    }
    history.pushState(
      { page: 1 },
      "Cars | BOTB",
      urlFilterParam.current === "" || urlFilterParam.current === "#!/" ? "/prizes/cars" : urlFilterParam.current
    );
  };

  function updateFilterValueInURL(key: string, newValue: string) {
    urlFilterParam.current = urlFilterParam.current.substring(0, urlFilterParam.current.length - 1);
    const regex = new RegExp(`(${key}=)[^&]+`);
    urlFilterParam.current = urlFilterParam.current.replace(regex, `$1${newValue}`);
  }

  const onFilterChange = (category: keyof Car, value: string) => {
    const filteredCarPrizes = getFilteredCars("", null, initialRangeFilterParams);
    if (category === "Styles") {
      dispatch(
        publishFilteredCars({
          cars: filteredCarPrizes.filter(prize => prize.Styles.includes(value)),
        })
      );
    } else {
      dispatch(
        publishFilteredCars({
          cars: filteredCarPrizes.filter(prize => prize.Make === value),
        })
      );
    }
    dispatch(
      publishFilterValues({
        value: {
          category,
          value,
        },
      })
    );
  };

  const onResetFilter = () => {
    rangeFilters.current = { ...initialRangeFilterParams };
    selectedFilter = null;
    dispatch(publishTicketPrice({ value: [] }));
    dispatch(publishRRP({ value: [] }));
    dispatch(publishTopMPH({ value: [] }));
    dispatch(publishZeroTo60({ value: [] }));
    dispatch(publishBHP({ value: [] }));
    dispatch(publishFilterValues({ value: null }));
    const searchCarClearButton = document.querySelector(".clear-icon") as HTMLDivElement;
    // check if the clear button is present and click it
    if (searchCarClearButton) {
      searchCarClearButton.click();
    }
    onSort("Sort By");
    history.pushState({ page: 1 }, "Cars | BOTB", "/prizes/cars");
    urlFilterParam.current = "";
  };

  const onCarSelect = (selectedCar: string) => {
    const rangeParams = getParams("", []);
    const filteredCarPrizes = getFilteredCars(selectedCar, selectedFilter, rangeParams);
    searchedCar.current = selectedCar;
    updateURLWithFilterParameters("search", selectedCar, selectedCar === "");
    dispatch(publishFilteredCars({ cars: filteredCarPrizes }));
  };

  const onSort = (selectedCategory: string) => {
    const carList = getFilteredCars(searchedCar.current, selectedFilter, rangeFilters.current);
    switch (selectedCategory) {
      case "price-high-to-low":
        carList.sort((car1, car2) => car2.ActiveTicket.Price - car1.ActiveTicket.Price);
        break;
      case "price-low-to-high":
        carList.sort((car1, car2) => car1.ActiveTicket.Price - car2.ActiveTicket.Price);
        break;
      case "a-to-z":
        carList.sort((car1, car2) => car1.Make.localeCompare(car2.Make));
        break;
      case "z-to-a":
        carList.sort((car1, car2) => car2.Make.localeCompare(car1.Make));
        break;
    }
    setSortByDropdownValue(selectedCategory);
    updateURLWithFilterParameters("sort", selectedCategory, selectedCategory === "Sort By");
    dispatch(publishFilteredCars({ cars: carList }));
  };

  const resetDummySpanElements = () => {
    const dummyMakeSpan = document.querySelector(".pre-select-make") as HTMLSpanElement;
    if (dummyMakeSpan) {
      dummyMakeSpan.textContent = "";
    }
    const dummyStyleSpan = document.querySelector(".pre-select-style") as HTMLSpanElement;
    if (dummyStyleSpan) {
      const selectedStyle = dummyStyleSpan.textContent;
      return {
        category: "Styles",
        value: selectedStyle,
      };
    }
  };

  const getAutoCompleteList = () => {
    const uniqueMakes = [...new Set(carPrizes.map(car => car.Make))];
    const uniqueStyles = [...new Set(carPrizes.map(car => car.Styles).flat())];
    const makeModels = carPrizes.map(car => `${car.Make} ${car.Model}`);
    const searchSuggestions = [...uniqueMakes, ...uniqueStyles, ...makeModels];
    return searchSuggestions.map((item, index) => ({ id: index, name: item }));
  };

  return (
    <>
      <div
        style={ithInView ? {} : { top: `${headerHeight}px` }}
        className={`${styles.lgView} p-8 flex flex-col justify-center items-center ${ithInView ? "" : styles.filtersDivNotInView}`}>
        <div className="flex gap-6 w-full justify-center items-center">
          <SelectDropdown
            defaultOptionValue="Sort By"
            defaultOptionText="Sort By"
            dropdownOptions={SORT_FILTER_OPTIONS}
            onChange={onSort}
            selectedValue={sortByDropdownValue}
            optionsParentDivClassName="w-36 left-0"
            dropdownParentDivClassName="w-36 p-1 self-center"
          />
          <SelectDropdown
            defaultOptionText="All Makes"
            customOptions={<CarMakes {...{ makeList }} />}
            optionsParentDivClassName="w-max -left-48"
            dropdownParentDivClassName="w-40 p-1 self-center"
            onChange={() => {
              resetDummySpanElements();
              location.href = `/prizes/cars${urlFilterParam.current}`;
            }}
            selectedValue={selectedFilter !== null && selectedFilter.category === "Make" ? "Makes (1)" : "All Makes"}
            defaultOptionValue="All Makes"
          />
          <SelectDropdown
            defaultOptionText="All Styles"
            customOptions={<CarStyles {...{ styleList }} />}
            optionsParentDivClassName="w-max -left-80"
            dropdownParentDivClassName="w-40 p-1 self-center"
            onChange={() => {
              resetDummySpanElements();
              location.href = `/prizes/cars${urlFilterParam.current}`;
            }}
            selectedValue={
              selectedFilter !== null && selectedFilter.category === "Styles" ? "Styles (1)" : "All Styles"
            }
            defaultOptionValue="All Styles"
          />
          <RangeFilter
            label={"Ticket Price (£)"}
            initialValue={initialTicketPriceRange}
            onRangeFilterChange={onRangeChange}
            path={ACTIVE_PRICE_PATH}
            className="w-40"
          />
          <div>
            <button
              aria-label="Reset Filter"
              className={`self-center p-[1rem] text-white flex justify-center rounded-full ${styles.resetFiltersBtn}`}
              onClick={onResetFilter}
              title="Reset">
              <img
                src="/assets/images/icon-reset.png"
                alt="reset-filters"
                width={30}
                height={30}
                className="self-center absolute p-1"
              />
            </button>
          </div>
          <div className="w-80 p-1 self-center">
            <ReactSearchAutocomplete
              styling={{ borderRadius: "5px", boxShadow: "none", zIndex: 1 }}
              items={getAutoCompleteList()}
              placeholder={"Search by Make, Model and Style"}
              className={"reactSearchAutoComplete car-make-model-autocomplete"}
              onSelect={item => onCarSelect(item.name)}
              onClear={() => onCarSelect("")}
              inputSearchString={searchedCar.current}
              fuseOptions={{
                threshold: 0.3,
              }}
            />
          </div>
          <div className="flex cursor-pointer" onClick={() => setMoreFiltersVisible(isVisible => !isVisible)}>
            <label
              aria-labelledby="More Filters"
              className={`self-center cursor-pointer  text-[#ff8200] ${styles.moreLink}`}>
              MORE
            </label>
            <img
              src="/assets/images/select-arrow.png"
              alt="arrow-dropdown"
              className="p-3 self-center "
              data-testid={`dropdown-state-down`}
            />
          </div>
        </div>
        {moreFiltersVisible && (
          <div className="flex gap-14 justify-center mt-8">
            <RangeFilter
              label={"RRP (£'000)"}
              initialValue={[initialRetailPriceGBPRange[0] / 1000, initialRetailPriceGBPRange[1] / 1000]}
              onRangeFilterChange={onRangeChange}
              path={RETAIL_PRICE_GBP_PATH}
              className="w-52"
            />
            <RangeFilter
              label={"Top MPH"}
              initialValue={initialTopMPHRange}
              onRangeFilterChange={onRangeChange}
              path={MPH_PATH}
              className="w-52"
            />
            <RangeFilter
              label={"0-60 (s)"}
              initialValue={initialZeroTo60Range}
              onRangeFilterChange={onRangeChange}
              path={TOP_SPEED_PATH}
              className="w-52"
            />
            <RangeFilter
              label={"BHP"}
              initialValue={initialPowerRange}
              onRangeFilterChange={onRangeChange}
              path={POWER_PATH}
              className="w-52"
            />
          </div>
        )}
      </div>
      <div className={`${styles.smView} flex items-center justify-center py-6`}>
        <ReactSearchAutocomplete
          styling={{ borderRadius: "5px", boxShadow: "none", zIndex: 1 }}
          items={getAutoCompleteList()}
          placeholder={"Search"}
          className={"reactSearchAutoComplete w-64"}
          onSelect={item => onCarSelect(item.name)}
          onClear={() => onCarSelect("")}
          inputSearchString={searchedCar.current}
          fuseOptions={{
            threshold: 0.3,
          }}
        />
        <SelectDropdown
          isSmView={true}
          defaultOptionValue="Sort By"
          defaultOptionText="Sort By"
          dropdownOptions={SORT_FILTER_OPTIONS}
          onChange={onSort}
          selectedValue={sortByDropdownValue}
          optionsParentDivClassName="w-48 left-0 top-9"
          dropdownParentDivClassName="px-2 self-center mx-2 w-10 h-10"
        />
        <FiltersForSmallerScreens
          makeList={makeList}
          styleList={styleList}
          onRangeFilterChange={onRangeChange}
          onClearAll={onResetFilter}
          rangeSliderList={[
            {
              label: "Ticket Price (£)",
              initialValue: initialTicketPriceRange,

              path: ACTIVE_PRICE_PATH,
            },
            {
              label: "RRP (£'000)",
              initialValue: [initialRetailPriceGBPRange[0] / 1000, initialRetailPriceGBPRange[1] / 1000],
              path: RETAIL_PRICE_GBP_PATH,
            },
            {
              label: "Top MPH",
              initialValue: initialTopMPHRange,
              path: MPH_PATH,
            },
            {
              label: "0-60 (s)",
              initialValue: initialZeroTo60Range,
              path: TOP_SPEED_PATH,
            },
            {
              label: "BHP",
              initialValue: initialPowerRange,
              path: POWER_PATH,
            },
          ]}
        />
      </div>
    </>
  );
};

export default OurCarsFilters;
